import { useEffect, useState } from "react";
import {
  completeOnboarding,
  type BillingPlan,
  type CompleteOnboardingResp,
  type ExhibitAPayload,
  type OnboardingPricing,
} from "../lib/api";
import { recurringLabel } from "../lib/billingPlan";
import { env } from "../env";

interface Props {
  uid: string;
  token: string;
  signedPdfBase64: string;
  signerFullName: string;
  exhibitA: ExhibitAPayload | null;
  paymentIntentId: string;
  /** Sign-in email surfaced on the success card. */
  loginEmail: string | null;
  /**
   * FF app login password (pinCode). Null for admin-invited users.
   */
  password: string | null;
  /** Used when arriving via "resume": skip the call and show success immediately. */
  alreadyComplete?: boolean;
  /**
   * Set when a partner code saved the card and collected activation plus
   * the first month offline. Omit on every other checkout.
   */
  partnerName?: string | null;
  pricing: OnboardingPricing;
  /** Null when resuming a finished enrollment on a device that never saw the choice. */
  billingPlan: BillingPlan | null;
  /**
   * Self-signup accounts are created inactive until staff activates them
   * after the device arrives. Admin-invited enrollments stay active.
   */
  testingDeferred?: boolean;
  /**
   * Set when the mobile app opened this enrollment. The done screen then
   * tells the user to close the browser sheet and sign in, instead of
   * offering store badges and a deep link.
   */
  openedFromApp?: boolean;
}

export function DoneStep(props: Props) {
  const [status, setStatus] = useState<
    "submitting" | "success" | "error"
  >(props.alreadyComplete ? "success" : "submitting");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CompleteOnboardingResp | null>(null);

  useEffect(() => {
    if (props.alreadyComplete) return;
    if (!props.exhibitA) {
      setError(
        "Missing Exhibit A data on this device — please re-open the link from your email or app to re-sign.",
      );
      setStatus("error");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await completeOnboarding({
          uid: props.uid,
          token: props.token,
          paymentIntentId: props.paymentIntentId,
          signedPdfBase64: props.signedPdfBase64,
          signerFullName: props.signerFullName,
          exhibitA: props.exhibitA!,
        });
        if (!cancelled) {
          setResult(resp);
          setStatus("success");
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(String((err as Error)?.message ?? err));
          setStatus("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    props.alreadyComplete,
    props.uid,
    props.token,
    props.paymentIntentId,
    props.signedPdfBase64,
    props.signerFullName,
    props.exhibitA,
  ]);

  if (status === "submitting") {
    return (
      <div className="py-16 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        <p className="mt-4 text-sm text-slate-600">
          Finalizing your enrollment…
        </p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-red-200 bg-red-50 p-5">
          <h2 className="text-lg font-semibold text-red-900">
            We hit a snag finalizing your enrollment
          </h2>
          <p className="mt-1 text-sm text-red-800">{error}</p>
          <p className="mt-3 text-sm text-red-800">
            Your payment was processed. Please tap retry — if it keeps failing,
            contact AMS support and reference Payment {props.paymentIntentId}.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setStatus("submitting");
            setError(null);
          }}
          className="w-full rounded-lg bg-brand-500 px-4 py-3 text-white font-semibold hover:bg-brand-600"
        >
          Retry
        </button>
      </div>
    );
  }

  const trialEndsAtSec = result?.trialEndsAtUnix ?? null;
  const trialEndsLabel = trialEndsAtSec
    ? new Date(trialEndsAtSec * 1000).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;
  const billingPlan = result?.billingPlan ?? props.billingPlan;

  return (
    <div className="space-y-6 text-center">
      <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          className="h-8 w-8 text-emerald-600"
          aria-hidden="true"
        >
          <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">
          You're enrolled
        </h2>
        <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
          {props.partnerName
            ? "Your card is saved. Activation fee and first month will be collected."
            : "Your lease is signed and your first month plus activation fee has been charged. Welcome to AMS!"}
        </p>
      </div>

      <div className="text-left rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-slate-600">Lease</span>
          <span className="font-medium text-slate-900">Signed and stored</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-slate-600">
            {props.partnerName ? "Activation fee and first month" : "First payment"}
          </span>
          <span className="font-medium text-slate-900 text-right">
            {props.partnerName ? "Will be collected" : "Successful"}
          </span>
        </div>
        {billingPlan ? (
          <div className="flex justify-between gap-4">
            <span className="text-slate-600">Next charge</span>
            <span className="font-medium text-slate-900 text-right">
              {recurringLabel(billingPlan, props.pricing)}
              {trialEndsLabel ? `, starting ${trialEndsLabel}` : ""}
            </span>
          </div>
        ) : null}
      </div>

      {props.loginEmail && props.password ? (
        <CredentialsCard
          email={props.loginEmail}
          password={props.password}
        />
      ) : null}

      {props.openedFromApp ? (
        <div className="text-left rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900 text-center">
            Return to the app
          </h3>
          <p className="mt-2 text-sm text-slate-600 text-center">
            Tap Done at the top of this screen, or the X on Android, to return
            to the app. Then sign in with the email and login code above.
          </p>
        </div>
      ) : (
        <>
          <DownloadAppCard testingDeferred={props.testingDeferred === true} />
          <a
            href={env.appDeepLink}
            className="inline-block text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            Already installed? Open the app
          </a>
        </>
      )}
      <p className="text-xs text-slate-500">
        You can close this page now. A confirmation email is on its way.
      </p>
    </div>
  );
}

const APP_STORE_URL =
  "https://apps.apple.com/us/app/alcohol-monitoring-solutions/id6739577526";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.alcoholmonitoringsolutions.app";

/**
 * Download card shown on the confirmation screen. Uses the official Apple +
 * Google badge images served from their own CDNs so we stay inside each
 * vendor's brand guidelines without bundling the artwork ourselves.
 */
function DownloadAppCard({ testingDeferred }: { testingDeferred: boolean }) {
  return (
    <div className="text-left rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="text-center">
        <h3 className="text-sm font-semibold text-slate-900">
          Download the AMS app
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          {testingDeferred
            ? "Your account is inactive until your device arrives. Install the app now — scheduled testing starts after AMS activates your account."
            : "Sign in with the email and app login code (pinCode) above to start testing."}
        </p>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download on the App Store"
          className="inline-block transition hover:opacity-90"
        >
          <img
            src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
            alt="Download on the App Store"
            className="h-12 w-auto"
            loading="lazy"
          />
        </a>
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Get it on Google Play"
          className="inline-block transition hover:opacity-90"
        >
          <img
            src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
            alt="Get it on Google Play"
            className="h-[58px] w-auto"
            loading="lazy"
          />
        </a>
      </div>
    </div>
  );
}

/**
 * Surfaces the auto-generated sign-in credentials on the confirmation screen.
 * The password is also delivered via email, but showing it here lets the user
 * copy it straight into the mobile app without bouncing through their inbox.
 */
function CredentialsCard({
  email,
  password,
}: {
  email: string;
  password: string;
}) {
  const [copied, setCopied] = useState<"email" | "password" | "both" | null>(
    null,
  );

  async function copy(kind: "email" | "password" | "both", value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /* clipboard not available — silently no-op; user can still read & type */
    }
  }

  return (
    <div className="text-left rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">
          Your AMS app sign-in
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          Use these to sign into the AMS mobile app. We also emailed them to
          you — save your pinCode somewhere safe before leaving this page.
        </p>
      </div>

      <div className="space-y-2">
        <CredentialRow
          label="Email"
          value={email}
          copied={copied === "email"}
          onCopy={() => copy("email", email)}
          mono={false}
        />
        <CredentialRow
          label="App login code (pinCode)"
          value={password}
          copied={copied === "password"}
          onCopy={() => copy("password", password)}
          mono
        />
      </div>

      <button
        type="button"
        onClick={() => copy("both", `${email}\n${password}`)}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
      >
        {copied === "both" ? "Copied both" : "Copy email & pinCode"}
      </button>
    </div>
  );
}

function CredentialRow({
  label,
  value,
  copied,
  onCopy,
  mono,
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
  mono: boolean;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
        {label}
      </div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <div
          className={
            "text-sm text-slate-900 break-all select-all " +
            (mono
              ? "font-mono text-base tracking-[0.18em] font-semibold"
              : "")
          }
        >
          {value}
        </div>
        <button
          type="button"
          onClick={onCopy}
          className="shrink-0 rounded-md bg-white px-2 py-1 text-xs font-medium text-brand-600 border border-slate-300 hover:bg-brand-50 hover:border-brand-300"
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}
